const PRIORITY_NORMAL = 0 as const;
const PRIORITY_PROMOTED = 1 as const;
type ItemPriorityType = typeof PRIORITY_NORMAL | typeof PRIORITY_PROMOTED;

type PendingItemType = {
  readonly key: string;
  readonly onReady: () => void;
  priority: ItemPriorityType;
  readonly insertionOrder: number;
};

export class ImageLoadScheduler {
  private readonly maxConcurrent: number;
  private readonly active = new Set<string>();
  private readonly pending: PendingItemType[] = [];
  private insertionCounter = 0;

  constructor(maxConcurrent = 10) {
    this.maxConcurrent = Math.max(1, maxConcurrent);
  }

  enqueue(id: string, onReady: () => void): void {
    if (this.active.has(id) || this.pending.some((p) => p.key === id)) {
      return;
    }

    if (this.active.size < this.maxConcurrent) {
      this.active.add(id);
      try {
        onReady();
      } catch (caughtError) {
        this.active.delete(id);
        console.error(
          JSON.stringify({
            message: "[ImageLoadScheduler] onReady callback failed",
            id,
          }),
          caughtError,
        );
        this.startNext();
      }
      return;
    }

    this.pending.push({
      key: id,
      onReady,
      priority: PRIORITY_NORMAL,
      insertionOrder: this.insertionCounter++,
    });
  }

  promote(id: string): void {
    if (this.active.has(id)) {
      return;
    }

    const item = this.pending.find((p) => p.key === id);
    if (item !== undefined) {
      item.priority = PRIORITY_PROMOTED;
    }
  }

  release(id: string): void {
    if (!this.active.delete(id)) {
      return;
    }

    this.startNext();
  }

  cancel(id: string): void {
    if (this.active.has(id)) {
      this.release(id);
      return;
    }

    const index = this.pending.findIndex((p) => p.key === id);
    if (index !== -1) {
      this.pending.splice(index, 1);
    }
  }

  private startNext(): void {
    if (this.pending.length === 0) {
      return;
    }

    let bestIndex = 0;
    for (let i = 1; i < this.pending.length; i++) {
      const best = this.pending[bestIndex];
      const current = this.pending[i];
      if (best === undefined || current === undefined) {
        continue;
      }
      if (
        current.priority > best.priority ||
        (current.priority === best.priority &&
          current.insertionOrder < best.insertionOrder)
      ) {
        bestIndex = i;
      }
    }

    const [next] = this.pending.splice(bestIndex, 1);
    if (next === undefined) {
      return;
    }
    this.active.add(next.key);
    try {
      next.onReady();
    } catch (caughtError) {
      this.active.delete(next.key);
      console.error(
        JSON.stringify({
          message: "[ImageLoadScheduler] onReady callback failed",
          id: next.key,
        }),
        caughtError,
      );
      this.startNext();
    }
  }
}

export const imageLoadScheduler = new ImageLoadScheduler(10);
